import { FeeTier, MarketType, OrderParamsBitFlag } from '@drift-labs/sdk';
import { cj } from 'src/utils/classNames';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { Open } from '@drift-labs/icons';
import useDriftClient from 'src/hooks/useDriftClient';
import React, { useEffect, useMemo, useState } from 'react';
import { getIsZeroFeesActiveForMarket } from 'src/utils/zeroFees';
import useDriftAccountsStore from 'src/stores/useDriftAccountsStore';
import { useShallow } from 'zustand/react/shallow';
import useModal from 'src/components/Modals/hooks/useModal';
import { COMMON_UI_UTILS } from '@drift/common';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import { useCurrentUserMarginMode } from 'src/hooks/user/useCurrentUserMarginMode';
import useGetDisplayLeverageForMarket from 'src/hooks/useGetDisplayLeverageForMarket';

interface Props {
	className?: string;
}

const FeeTierValueDisplay = React.memo(function FeeTierValueDisplay({
	className,
}: Props) {
	const selectedMarketId = useDriftStore((s) => s.selectedMarket.marketId);
	const { show } = useModal();
	const userAccountClient = useDriftAccountsStore(
		(s) => s.accounts[s.currentUserKey ?? '']?.client
	);
	const isMobileScreenSize = useIsMobileScreenSize();
	const driftClient = useDriftClient();
	const [tradeSide, postOnly] = useDriftStore(
		useShallow((s) => [s.tradeForm.side, s.tradeForm.postOnly])
	);
	const highLeverageSpotsOpen = useDriftStore(
		(s) => s.highLeverageSpots.spotsOpen
	);
	const [feeTier, setFeeTier] = useState<FeeTier>();

	// Track marginMode to trigger re-render when high leverage mode changes
	const marginMode = useCurrentUserMarginMode();

	// Calculate display leverage for getBitFlags
	const displayLeverage = useGetDisplayLeverageForMarket(
		selectedMarketId.marketIndex
	);

	const isZeroFeesPromotion = getIsZeroFeesActiveForMarket({
		marketIndex: selectedMarketId.marketIndex,
		isPerp: selectedMarketId.isPerp,
	});

	useEffect(() => {
		const getFeeTier = async () => {
			try {
				const tier = userAccountClient?.getUserFeeTier(MarketType.PERP);
				setFeeTier(tier);
			} catch (e) {
				try {
					const statsAccount = driftClient?.getUserStats();
					await statsAccount?.subscribe();
					const tier = userAccountClient?.getUserFeeTier(MarketType.PERP);
					setFeeTier(tier);
				} catch (err) {
					console.error('Error getting user fee tier after subscribe', err);
					setFeeTier(undefined);
				}
			}
		};

		getFeeTier();
	}, [userAccountClient, driftClient]);

	const feeTierContent = useMemo(() => {
		if (!userAccountClient || !feeTier) {
			return null;
		}

		const {
			feeNumerator,
			feeDenominator,
			makerRebateNumerator,
			makerRebateDenominator,
		} = feeTier;

		// When in HLM, user is set to first fee tier and we 2x the taker fee.
		const isHighLeverageModeActive =
			userAccountClient?.isHighLeverageMode('Initial');
		const isEnteringHighLeverageMode =
			driftClient &&
			userAccountClient &&
			COMMON_UI_UTILS.getPerpOrderParamsBitFlags(
				selectedMarketId.marketIndex,
				driftClient,
				userAccountClient,
				displayLeverage
			) === OrderParamsBitFlag.UpdateHighLeverageMode;

		const highLeverageMultiplier =
			isHighLeverageModeActive || isEnteringHighLeverageMode ? 2 : 1;

		const takerFeePct =
			(feeNumerator / feeDenominator) * highLeverageMultiplier * 100;

		const makerFeePct = -(makerRebateNumerator / makerRebateDenominator) * 100;

		const takerFeePctFormatted = parseFloat(takerFeePct.toFixed(4));
		// Maker fee intentionally formatted with a different precision.
		const makerFeePctFormatted = parseFloat(makerFeePct.toFixed(5));

		if (isZeroFeesPromotion) {
			return (
				<>
					<span className="text-text-secondary line-through">
						{postOnly ? makerFeePctFormatted : takerFeePctFormatted}%
					</span>
					<span className="text-star-yellow">
						0% {!isMobileScreenSize && '/ 0%'}
					</span>
				</>
			);
		}

		if (!isMobileScreenSize) {
			return `${takerFeePctFormatted}% / ${makerFeePctFormatted}%`;
		}

		if (!postOnly) {
			return `${takerFeePctFormatted}%`;
		}

		return `${makerFeePctFormatted}%`;
	}, [
		selectedMarketId.marketIndex,
		driftClient,
		userAccountClient,
		feeTier,
		displayLeverage,
		tradeSide,
		isZeroFeesPromotion,
		isMobileScreenSize,
		postOnly,
		highLeverageSpotsOpen,
		marginMode, // marginMode is added here as a dependency to trigger a re-render when the user's margin mode changes
	]);

	const handleClick = () => {
		show({ type: 'fee_tier', props: {} });
	};

	return (
		<div className={cj('flex items-center justify-between typo-b4', className)}>
			<span className="text-text-secondary">Fees</span>
			<span className="flex items-center gap-x-xs text-text-default">
				{feeTierContent ?? '-'}
				{feeTierContent && (
					<IconButton
						size="sm"
						variant="tertiary"
						className="-m-sm p-sm"
						onClick={handleClick}
						icon={Open}
					/>
				)}
			</span>
		</div>
	);
});

export default FeeTierValueDisplay;
